import React, { useContext, useEffect, useState } from "react";
import { Row, Col } from "antd";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { UserDataContext } from "../context/User";
import axios from "axios";
import {
    CATEGORY_LABELS,
    formatAccessList,
    formatEntryFee,
    formatQuestDateRange,
    getCompactQuestFields,
    getQuestImages,
    getQuestStatus,
    getQuestSummary,
} from "../utils/questDisplay";
 
const TABS = [
    { id: "quests", label: "Quests" },
    { id: "events", label: "Events" },
    { id: "showcases", label: "Showcases" },
];



function Challenges() {
    const { userData } = useContext(UserDataContext);
    const [activeTab, setActiveTab] = useState("quests");
    const [challenges, setChallenges] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const navigate = useNavigate();

    const getQuest = async (tab, pageNumber = 1, append = false) => {
        try {
            if (pageNumber === 1) setLoading(true);
            else setLoadingMore(true);

            const res = await axios.get(
                `/api/challenges/get?category=${tab}&page=${pageNumber}&limit=10`
            );
            const data = res.data;
            const pages = Math.ceil(data.total / data.limit);
            setTotalPages(pages);

            if (append) {
                setChallenges(prev => [...prev, ...data.quest_challenges]);
            } else {
                setChallenges(data.quest_challenges);
            }
        } catch (error) {
            console.error("API error:", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleReadMore = (id) => navigate(`/quests/${id}`);
    const handleSubmitEntry = (id) => navigate(`/submissions?challenge=${id}`);

    // Reset and fetch on tab change
    useEffect(() => {
        setPage(1);
        setChallenges([]);
        getQuest(activeTab, 1, false);
    }, [activeTab]);

    const handleViewMore = () => {
        if (page >= totalPages || loadingMore) return;
        const nextPage = page + 1;
        setPage(nextPage);
        getQuest(activeTab, nextPage, true);
    };
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                duration: 0.8
            }
        }
    };

    const cardVariants = {
        hidden: { 
            opacity: 0, 
            y: 60,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.7,
                ease: [0.25, 0.46, 0.45, 0.94]
            }
        },
        hover: {
            y: -5,
            scale: 1.02,
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            transition: {
                duration: 0.3,
                ease: "easeOut"
            }
        }
    };

    const imageVariants = {
        hidden: { 
            scale: 1.1, 
            opacity: 0.8,
            filter: "blur(4px)"
        },
        visible: {
            scale: 1,
            opacity: 1,
            filter: "blur(2px)",
            transition: {
                duration: 1,
                ease: "easeOut"
            }
        },
        hover: {
            scale: 1.5,
            filter: "blur(0px)",
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const buttonVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        },
        hover: {
            scale: 1.05,
            background: "linear-gradient(29deg, rgba(0, 240, 251, 1) 0%, rgba(255, 255, 255, 1) 30%, rgba(0, 240, 251, 1) 100%)",
            boxShadow: "0 10px 25px rgba(0, 240, 251, 0.4)",
            transition: {
                duration: 0.3,
                ease: "easeInOut"
            }
        },
        tap: {
            scale: 0.95,
            transition: {
                duration: 0.1
            }
        }
    };

    const tabVariants = {
        hidden: { scale: 0.95, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        },
        hover: {
            scale: 1.05,
            transition: {
                duration: 0.2
            }
        }
    };

    const textVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const iconVariants = {
        hover: {
            scale: 1.3,
            rotate: 5,
            transition: {
                duration: 0.3,
                ease: "easeOut"
            }
        }
    };
    return (
        <motion.section 
            className="main-lwbl bg-[#F9FAFB] py-16 px-[1rem] overflow-hidden rounded-[30px]"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={containerVariants}
        >
            <div className="max-w-6xl mx-auto px-4">
                {/* Heading */}
                <motion.h4 
                    className="font-outfit text-sm md:text-base text-center text-[#555]"
                    variants={textVariants}
                >
                    Stay connected with events, challenges, and community gatherings
                </motion.h4>
                
                <motion.h2 
                    className="font-cinzel text-[32px] text-center md:text-[40px] text-[#000] mt-1 mb-3"
                    variants={textVariants}
                >
                    What's Happening at LWBL
                </motion.h2>
                
                <motion.p 
                    className="font-outfit text-sm text-center md:text-base text-[#555]"
                    variants={textVariants}
                >
                    Creativity is always in motion. Our events calendar keeps you
                    apprised of every opportunity to shine. From quarterly Quests and
                    gallery exhibitions to interactive workshops and inspiring guest
                    speaker sessions, there's always something happening to spark your
                    imagination.
                </motion.p>

                {/* Tabs */}
                <motion.div 
                    className="mt-8 flex justify-center"
                    variants={textVariants}
                >
                    <div className="inline-flex rounded-full border border-[#D9D9D9] bg-white p-1 gap-1">
                        {TABS.map((tab) => (
                            <motion.button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-6 py-2 rounded-full text-sm font-outfit transition-all duration-200 ${activeTab === tab.id
                                        ? "bg-gradient-to-r from-[#00F0FB] to-[#00D1E5] text-white shadow-[0_4px_12px_rgba(0,0,0,0.18)]"
                                        : "bg-transparent text-[#111] hover:bg-[#F5F5F5]"
                                    }`}
                                variants={tabVariants}
                                whileHover="hover"
                            >
                                {tab.label}
                            </motion.button>
                        ))}
                    </div>
                </motion.div>

                {/* Cards */}
                <div className="mt-10">

                    {/* Loading state */}
                    {loading && (
                        <div className="flex justify-center py-16">
                            <div className="w-8 h-8 border-4 border-[#00F0FB] border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {/* Empty state */}
                    {!loading && challenges.length === 0 && (
                        <div className="text-center py-16">
                            <p className="font-outfit text-gray-400 text-base">
                                No {activeTab === "all" ? "quests or challenges" : activeTab} found at the moment. Check back soon!
                            </p>
                        </div>
                    )}

                    <Row gutter={[0, 24]}>
                        {challenges.map((item) => {
                            const status = getQuestStatus(item);
                            const access = formatAccessList(item.accessibility);
                            const fields = getCompactQuestFields(item.description);
                            const questImages = getQuestImages(item);
                            const cardImage = questImages[0];
                            const featuredFields = [
                                { label: "Schedule", value: formatQuestDateRange(item.startDate, item.endDate) },
                                { label: "Entry", value: formatEntryFee(item.entryFeeCents, item.currency) },
                                ...fields.slice(0, 2),
                            ];

                            return (
                            <Col span={24} key={item._id}>
                                <motion.div
                                    variants={cardVariants}
                                    whileHover="hover"
                                    className="flex flex-col md:flex-row items-stretch rounded-lg border border-[#DCE5E5] bg-white shadow-sm overflow-hidden relative"
                                >
                                    {/* Text side */}
                                    <div className="flex-1 px-6 py-6 md:px-8 md:py-8">
                                        <div className="mb-4 flex flex-wrap items-center gap-2">
                                            <span className={`rounded-full border px-3 py-1 font-outfit text-xs font-semibold ${status.className}`}>
                                                {status.label}
                                            </span>
                                            <span className="rounded-full border border-[#C9D7DA] bg-[#F7FAFA] px-3 py-1 font-outfit text-xs font-semibold uppercase tracking-[0.14em] text-[#415155]">
                                                {CATEGORY_LABELS[item.category] || "Quest"}
                                            </span>
                                        </div>

                                        <motion.h3 
                                            className="font-cinzel text-xl text-start md:text-4xl font-medium tracking-[0.08em] uppercase text-[#111]"
                                            whileHover={{ x: 5 }}
                                            style={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {item.title}
                                        </motion.h3>

                                        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                            {featuredFields.map((field) => (
                                                <div key={`${item._id}-${field.label}`} className="rounded-lg border border-[#E3EAEB] bg-[#F9FAFB] px-4 py-3">
                                                    <p className="font-outfit text-[11px] font-bold uppercase tracking-[0.14em] text-[#728083]">
                                                        {field.label}
                                                    </p>
                                                    <p
                                                        className="mt-1 font-outfit text-sm font-semibold text-[#182124]"
                                                        style={{
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        {field.value}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {access.slice(0, 4).map((tier) => (
                                                <span key={`${item._id}-${tier}`} className="rounded-full bg-[#E7FBFD] px-3 py-1 font-outfit text-xs font-semibold text-[#007D91]">
                                                    {tier}
                                                </span>
                                            ))}
                                        </div>
                                        
                                        <motion.p 
                                            className="font-outfit text-xs md:text-sm text-start text-[#444] mt-5 max-w-3xl leading-relaxed"
                                            whileHover={{ x: 5 }}
                                            style={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {getQuestSummary(item.description, 180)}
                                        </motion.p>

                                        <div className="mt-7 flex flex-wrap gap-3">
                                            <motion.button
                                                onClick={() => handleReadMore(item._id)}
                                                variants={buttonVariants}
                                                whileHover="hover"
                                                whileTap="tap"
                                                className="text-black font-outfit font-semibold px-6 py-3 rounded-full shadow-md relative overflow-hidden"
                                                style={{
                                                    background: "linear-gradient(29deg, rgba(0, 240, 251, 1) 0%, rgba(255, 255, 255, 1) 50%, rgba(0, 240, 251, 1) 100%)",
                                                }}
                                            >
                                                <span className="relative z-10">View Details</span>
                                                <motion.div
                                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
                                                    initial={{ x: "-100%" }}
                                                    whileHover={{ x: "100%" }}
                                                    transition={{ duration: 0.6 }}
                                                />
                                            </motion.button>

                                            {userData ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSubmitEntry(item._id)}
                                                    className="rounded-full border border-[#9FB9BE] px-6 py-3 font-outfit text-sm font-semibold text-[#223033] transition hover:border-[#00C3FF] hover:text-[#007D91]"
                                                >
                                                    Submit Entry
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate("/login")}
                                                    className="rounded-full border border-[#9FB9BE] px-6 py-3 font-outfit text-sm font-semibold text-[#223033] transition hover:border-[#00C3FF] hover:text-[#007D91]"
                                                >
                                                    Sign in to Enter
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Image side with scale animation */}
                                    <motion.div 
                                        className="relative w-full md:w-72 h-56 md:h-auto overflow-hidden"
                                        variants={imageVariants}
                                        whileHover="hover"
                                    >
                                        {cardImage ? (
                                            <motion.img
                                                src={cardImage}
                                                alt={item.title}
                                                className={`w-full h-full object-cover ${userData? "" :"blur-sm"} `}
                                                whileHover={{ scale: 1.5 }}
                                                transition={{ duration: 0.6 }}
                                            />
                                        ) : (
                                            <div className="h-full w-full bg-[#E7FBFD]" />
                                        )}
                                        
                                        {/* Lock overlay */}
                                        {userData? " ":(
                                             <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                                            <motion.div 
                                                className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center backdrop-blur-sm"
                                                whileHover="hover"
                                                variants={iconVariants}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    className="w-5 h-5"
                                                    fill="none"
                                                    stroke="#000"
                                                    strokeWidth="1.8"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <rect x="5" y="11" width="14" height="9" rx="2" />
                                                    <path d="M9 11V8a3 3 0 0 1 6 0v3" />
                                                </svg>
                                            </motion.div>
                                            
                                        </div>
                                        )}
                                        
                                    </motion.div>
                                    
                                </motion.div>
                                
                            </Col>
                            );
                        })}
                
                    </Row>

                    {/* View More Button — only show when more pages exist */}
                    {!loading && page < totalPages && (
                        <motion.button
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                            className="flex items-center gap-2 m-auto mt-[45px] text-black font-outfit font-semibold px-8 py-3 rounded-full shadow-md relative overflow-hidden disabled:opacity-60"
                            style={{
                                background: "linear-gradient(29deg, rgba(0, 240, 251, 1) 0%, rgba(255, 255, 255, 1) 50%, rgba(0, 240, 251, 1) 100%)",
                            }}
                            onClick={handleViewMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? (
                                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    <span className="relative z-10">View More</span>
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
                                        initial={{ x: "-100%" }}
                                        whileHover={{ x: "100%" }}
                                        transition={{ duration: 0.6 }}
                                    />
                                </>
                            )}
                        </motion.button>
                    )}
                </div>
            </div>
        </motion.section>
    );
}

export default Challenges;

